import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  AssignmentRounded,
  HourglassTopRounded,
  ListAltRounded,
  MiscellaneousServicesRounded,
  TranslateRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import ReportMetricCard from "components/reports/shared/ReportMetricCard";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";

// ─── helpers ────────────────────────────────────────────────────────────────

const MONTH_MAP = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

const fmt = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value || 0));

const KpiSkeleton = () => (
  <Grid container spacing={2} size={{ xs: 12 }}>
    {[0, 1, 2, 3].map((i) => (
      <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
        <Skeleton variant="rounded" height={120} />
      </Grid>
    ))}
  </Grid>
);

// ─── main component ──────────────────────────────────────────────────────────

const DashboardV2AllServices = ({ user, dateSelection }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartColors = getChartColors(theme);
  const { refreshPage, handleErrorDialog } = useStateContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [servicesKPIs, setServicesKPIs] = useState(null);

  const DRAWER_INITIAL = { open: false, loading: false, title: "", subtitle: "", data: [], columns: [], summary: null, onRowClick: null };
  const [drillDown, setDrillDown] = useState(DRAWER_INITIAL);
  const openDrillDown = (payload) => setDrillDown({ ...DRAWER_INITIAL, open: true, ...payload });
  const closeDrillDown = () => setDrillDown(DRAWER_INITIAL);
  const [serviceDemandTrends, setServiceDemandTrends] = useState(null);
  const [pendingChecks, setPendingChecks] = useState(null);
  const [topLanguages, setTopLanguages] = useState(null);

  const selectedYear = dateSelection?.years?.[0] || dayjs().year();
  const selectedMonths = useMemo(
    () => dateSelection?.months || [],
    [dateSelection?.months],
  );

  const params = useMemo(
    () => ({
      year: selectedYear,
      months: selectedMonths.map((m) => MONTH_MAP[m] || 1),
    }),
    [selectedYear, selectedMonths],
  );

  const periodLabel = useMemo(() => {
    if (!selectedMonths.length) return String(selectedYear);
    if (selectedMonths.length === 1) return `${selectedMonths[0]} ${selectedYear}`;
    return `${selectedMonths[0]}–${selectedMonths[selectedMonths.length - 1]} ${selectedYear}`;
  }, [selectedYear, selectedMonths]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .all([
        api.get("/dashboard/all/services/servicesCount", { params }),
        api.get("/dashboard/all/services/serviceDemandTrends", { params }),
        api.get("/dashboard/all/services/pendingChecks", { params }),
        api.get("/dashboard/all/services/topLanguages", { params }),
      ])
      .then(
        api.spread((kpis, trends, checks, langs) => {
          setServicesKPIs(kpis?.data ?? null);
          setServiceDemandTrends(trends?.data ?? null);
          setPendingChecks(checks?.data ?? null);
          setTopLanguages(langs?.data ?? null);
          setLoading(false);
        }),
      )
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          "Unable to load data. Please try again.";
        handleErrorDialog(msg, theme);
        setError(msg);
        setLoading(false);
      });
  }, [refreshPage, params, handleErrorDialog, theme]);

  // ── pie data ──────────────────────────────────────────────────────────────

  const interpStatusPie = [
    { id: 0, value: Number(servicesKPIs?.byStatus?.interpretation?.completed || 0), label: "Completed", color: chartColors.series[2] },
    { id: 1, value: Number(servicesKPIs?.byStatus?.interpretation?.confirmed || 0), label: "Confirmed", color: chartColors.series[0] },
    { id: 2, value: Number(servicesKPIs?.byStatus?.interpretation?.requested || 0), label: "Requested", color: chartColors.series[3] },
    { id: 3, value: Number(servicesKPIs?.byStatus?.interpretation?.cancelled || 0), label: "Cancelled", color: chartColors.series[4] },
  ];
  const transStatusPie = [
    { id: 0, value: Number(servicesKPIs?.byStatus?.translation?.completed || 0), label: "Completed", color: chartColors.series[2] },
    { id: 1, value: Number(servicesKPIs?.byStatus?.translation?.delivered || 0), label: "Delivered", color: chartColors.series[0] },
    { id: 2, value: Number(servicesKPIs?.byStatus?.translation?.inProgress || 0), label: "In Progress", color: chartColors.series[3] },
  ];
  const otherStatusPie = [
    { id: 0, value: Number(servicesKPIs?.byStatus?.otherService?.completed || 0), label: "Completed", color: chartColors.series[2] },
    { id: 1, value: Number(servicesKPIs?.byStatus?.otherService?.delivered || 0), label: "Delivered", color: chartColors.series[0] },
    { id: 2, value: Number(servicesKPIs?.byStatus?.otherService?.inProgress || 0), label: "In Progress", color: chartColors.series[3] },
  ];
  const interpTypePie = [
    { id: 0, value: Number(servicesKPIs?.byType?.interpretation?.inPerson || 0), label: "In Person", color: chartColors.series[0] },
    { id: 1, value: Number(servicesKPIs?.byType?.interpretation?.virtual || 0), label: "Virtual", color: chartColors.series[1] },
  ];
  const transTypePie = [
    { id: 0, value: Number(servicesKPIs?.byType?.translation?.translation || 0), label: "Translation", color: chartColors.series[0] },
    { id: 1, value: Number(servicesKPIs?.byType?.translation?.proofreading || 0), label: "Proofreading", color: chartColors.series[1] },
    { id: 2, value: Number(servicesKPIs?.byType?.translation?.transcreation || 0), label: "Transcreation", color: chartColors.series[3] },
  ];
  const otherTypePie = [
    { id: 0, value: Number(servicesKPIs?.byType?.otherService?.design || 0), label: "Design", color: chartColors.series[0] },
    { id: 1, value: Number(servicesKPIs?.byType?.otherService?.voiceover || 0), label: "Voiceover", color: chartColors.series[1] },
    { id: 2, value: Number(servicesKPIs?.byType?.otherService?.consulting || 0), label: "Consulting", color: chartColors.series[2] },
    { id: 3, value: Number(servicesKPIs?.byType?.otherService?.retainerCredited || 0), label: "Retainer Credited", color: chartColors.series[3] },
    { id: 4, value: Number(servicesKPIs?.byType?.otherService?.equipment || 0), label: "Equipment", color: chartColors.series[5] },
    { id: 5, value: Number(servicesKPIs?.byType?.otherService?.other || 0), label: "Other", color: chartColors.series[4] },
  ];

  // ── drill-down helpers ─────────────────────────────────────────────────────

  const SERVICE_COLUMNS = [
    { field: "projectNumber", headerName: "#", width: 80 },
    {
      field: "date",
      headerName: "Date",
      width: 110,
      renderCell: (row) => row.date ? dayjs(row.date).format("MM/DD/YYYY") : "—",
    },
    { field: "name", headerName: "Name", width: 160 },
    { field: "client", headerName: "Client", width: 180 },
    { field: "linguist", headerName: "Linguist", width: 160 },
    { field: "serviceType", headerName: "Type", width: 160 },
    { field: "status", headerName: "Status", width: 140 },
    {
      field: "accountCharge",
      headerName: "Charge",
      width: 110,
      renderCell: (row) => `$${Number(row.accountCharge || 0).toFixed(2)}`,
    },
  ];

  const fetchAndOpenDrillDown = (meta, apiParams) => {
    setDrillDown({ ...DRAWER_INITIAL, open: true, loading: true, onRowClick: openServiceProfileFromRow, ...meta });
    api
      .get("/dashboard/all/services/servicesByStatus", { params: apiParams })
      .then((res) => {
        const records = res?.data || [];
        setDrillDown((prev) => ({ ...prev, loading: false, data: records, columns: SERVICE_COLUMNS }));
      })
      .catch(() => {
        setDrillDown((prev) => ({ ...prev, loading: false }));
      });
  };

  const openServiceProfileFromRow = (row) => {
    if (!row?.id) return;
    navigate(`/services/${row.id}`, { state: { ComingFrom: "/dashboard" } });
  };

  const openBarDrillDown = (chartTitle, barData, item) => {
    const rank = barData.findIndex((d) => d.name === item.name) + 1;
    openDrillDown({
      title: chartTitle,
      subtitle: `Selected: ${item.name}`,
      summary: {
        metrics: [
          { label: "Language", value: item.name },
          { label: "Value", value: fmt(item.value), color: theme.palette.primary.main },
          { label: "Rank", value: `#${rank}` },
        ],
      },
      data: barData.map((d, i) => ({ rank: i + 1, name: d.name, value: d.value })),
      columns: [
        { field: "rank", headerName: "#", width: 50 },
        { field: "name", headerName: "Language", width: 200 },
        { field: "value", headerName: "Value", width: 120 },
      ],
    });
  };

  // ── demand trends line chart ───────────────────────────────────────────────
  // ── render ────────────────────────────────────────────────────────────────

  if (error)
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  return (
    <>
    <Box sx={{ p: 1, pb: 3 }}>
      {/* Period badge */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Chip label={periodLabel} size="small" color="primary" variant="outlined" />
        <Typography variant="body2" color="text.secondary">
          Service counts and trends for the selected period
        </Typography>
      </Box>

      {/* KPI Row */}
      {loading ? (
        <KpiSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ReportMetricCard
              title="Total Services"
              value={fmt(servicesKPIs?.totalServices)}
              icon={<ListAltRounded />}
              color="primary"
              tooltipTitle="All services across all categories"
              onClick={() =>
                fetchAndOpenDrillDown(
                  {
                    title: "Total Services",
                    subtitle: periodLabel,
                    summary: {
                      metrics: [
                        { label: "Interpretations", value: fmt(servicesKPIs?.totalInterpretations), color: theme.palette.info.main },
                        { label: "Translations", value: fmt(servicesKPIs?.totalTranslations), color: theme.palette.warning.main },
                        { label: "Other Services", value: fmt(servicesKPIs?.totalOtherServices), color: theme.palette.success.main },
                      ],
                    },
                  },
                  { ...params },
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ReportMetricCard
              title="Interpretations"
              value={fmt(servicesKPIs?.totalInterpretations)}
              icon={<AssignmentRounded />}
              color="info"
              tooltipTitle="Interpretation services in the selected period"
              onClick={() => fetchAndOpenDrillDown(
                { title: "Interpretations", subtitle: periodLabel, summary: { metrics: [{ label: "Total", value: fmt(servicesKPIs?.totalInterpretations || 0), color: theme.palette.info.main }] } },
                { ...params, category: "Interpretation" },
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ReportMetricCard
              title="Translations"
              value={fmt(servicesKPIs?.totalTranslations)}
              icon={<TranslateRounded />}
              color="warning"
              tooltipTitle="Translation services in the selected period"
              onClick={() => fetchAndOpenDrillDown(
                { title: "Translations", subtitle: periodLabel, summary: { metrics: [{ label: "Total", value: fmt(servicesKPIs?.totalTranslations || 0), color: theme.palette.warning.main }] } },
                { ...params, category: "Translation" },
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ReportMetricCard
              title="Other Services"
              value={fmt(servicesKPIs?.totalOtherServices)}
              icon={<MiscellaneousServicesRounded />}
              color="success"
              tooltipTitle="Other services in the selected period"
              onClick={() => fetchAndOpenDrillDown(
                { title: "Other Services", subtitle: periodLabel, summary: { metrics: [{ label: "Total", value: fmt(servicesKPIs?.totalOtherServices || 0), color: theme.palette.success.main }] } },
                { ...params, category: "Other" },
              )}
            />
          </Grid>
        </Grid>
      )}

      {/* Pending Checks */}
      {!loading && (
        <>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Pending Checks</Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            {[
              { title: "Pending Interpretation Checks", value: pendingChecks?.byCategory?.interpretation, category: "Interpretation" },
              { title: "Pending Translation Checks", value: pendingChecks?.byCategory?.translation, category: "Translation" },
              { title: "Pending Other Service Checks", value: pendingChecks?.byCategory?.otherService, category: "Other" },
            ].map(({ title, value, category }) => (
              <Grid key={title} size={{ xs: 12, sm: 4 }}>
                <ReportMetricCard
                  title={title}
                  value={fmt(value)}
                  icon={<HourglassTopRounded />}
                  color="warning"
                  onClick={() =>
                    fetchAndOpenDrillDown(
                      {
                        title,
                        subtitle: `${periodLabel} • Pending Only`,
                        summary: {
                          metrics: [
                            { label: "Count", value: fmt(value || 0), color: theme.palette.warning.main },
                          ],
                        },
                      },
                      { ...params, category, pendingOnly: true },
                    )
                  }
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Status Distribution */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Status Distribution</Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        {[
          { title: "Interpretation Status", data: interpStatusPie, category: "Interpretation", filterMode: "status" },
          { title: "Translation Status", data: transStatusPie, category: "Translation", filterMode: "status" },
          { title: "Other Services Status", data: otherStatusPie, category: "Other", filterMode: "status" },
        ].map(({ title, data: pieData, category, filterMode }) => (
          <Grid key={title} size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title={title}
              data={pieData}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) => {
                const total = pieData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
                const pct = total > 0 ? ((Number(slice.value) / total) * 100).toFixed(1) : "0.0";
                fetchAndOpenDrillDown(
                  {
                    title,
                    subtitle: `${slice.label} — ${fmt(slice.value)} (${pct}%)`,
                    summary: {
                      metrics: [
                        { label: "Status", value: slice.label },
                        { label: "Count", value: fmt(slice.value), color: theme.palette.primary.main },
                        { label: "Share", value: `${pct}%`, color: theme.palette.info.main },
                      ],
                    },
                  },
                  { ...params, category, [filterMode]: slice.label },
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Service Type Mix */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Service Type Mix</Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        {[
          { title: "Interpretation Types", data: interpTypePie, category: "Interpretation", filterMode: "type" },
          { title: "Translation Types", data: transTypePie, category: "Translation", filterMode: "type" },
          { title: "Other Service Types", data: otherTypePie, category: "Other", filterMode: "type" },
        ].map(({ title, data: pieData, category, filterMode }) => (
          <Grid key={title} size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title={title}
              data={pieData}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) => {
                const total = pieData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
                const pct = total > 0 ? ((Number(slice.value) / total) * 100).toFixed(1) : "0.0";
                fetchAndOpenDrillDown(
                  {
                    title,
                    subtitle: `${slice.label} — ${fmt(slice.value)} (${pct}%)`,
                    summary: {
                      metrics: [
                        { label: "Type", value: slice.label },
                        { label: "Count", value: fmt(slice.value), color: theme.palette.primary.main },
                        { label: "Share", value: `${pct}%`, color: theme.palette.info.main },
                      ],
                    },
                  },
                  { ...params, category, [filterMode]: slice.label },
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Demand Trends */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Service Demand Trends</Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12 }}>
          <ReportLineChart
            title="Daily Service Volume"
            xAxisData={serviceDemandTrends?.days || []}
            series={[
              { data: serviceDemandTrends?.totalServices || [], label: "Total", color: chartColors.series[0] },
              { data: serviceDemandTrends?.interpretationServices || [], label: "Interpretations", color: chartColors.series[1] },
              { data: serviceDemandTrends?.translationServices || [], label: "Translations", color: chartColors.series[3] },
              { data: serviceDemandTrends?.otherServices || [], label: "Other", color: chartColors.series[2] },
            ]}
            height={420}
            onMarkClick={(dataIndex, dayLabel) => {
              const total = (serviceDemandTrends?.totalServices || [])[dataIndex] || 0;
              const interp = (serviceDemandTrends?.interpretationServices || [])[dataIndex] || 0;
              const trans = (serviceDemandTrends?.translationServices || [])[dataIndex] || 0;
              const other = (serviceDemandTrends?.otherServices || [])[dataIndex] || 0;
              openDrillDown({
                title: "Daily Service Volume",
                subtitle: `Day: ${dayLabel}`,
                summary: {
                  metrics: [
                    { label: "Total", value: fmt(total), color: theme.palette.primary.main },
                    { label: "Interpretations", value: fmt(interp), color: theme.palette.info.main },
                    { label: "Translations", value: fmt(trans), color: theme.palette.warning.main },
                    { label: "Other", value: fmt(other) },
                  ],
                },
                data: [
                  { category: "Interpretations", count: interp },
                  { category: "Translations", count: trans },
                  { category: "Other", count: other },
                  { category: "Total", count: total },
                ],
                columns: [
                  { field: "category", headerName: "Category", width: 200 },
                  { field: "count", headerName: "Count", width: 120 },
                ],
              });
            }}
          />
        </Grid>
      </Grid>

      {/* Top Languages */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Top Languages</Typography>
      <Grid container spacing={2} sx={{ pb: 2 }}>
        {[
          { title: "Top 15 by Number of Orders", data: topLanguages?.byTotalCount },
          { title: "Top 15 by Account Charge", data: topLanguages?.byTotalAccountCharge },
          { title: "Top 15 by Profit", data: topLanguages?.byTotalProfit },
        ].map(({ title, data: langData }) => {
          const barData = (langData || []).map((d) => ({ name: d.name, value: d.value }));
          return (
            <Grid key={title} size={{ xs: 12 }}>
              <ReportBarChart
                title={title}
                data={barData}
                xAxisKey="name"
                series={[{ dataKey: "value", label: "Count", color: chartColors.series[0] }]}
                height={340}
                chartMargin={{ top: 20, right: 20, bottom: 100, left: 60 }}
                onBarClick={(item) => openBarDrillDown(title, barData, item)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
    <DrillDownDrawer
      open={drillDown.open}
      onClose={closeDrillDown}
      title={drillDown.title}
      subtitle={drillDown.subtitle}
      data={drillDown.data}
      columns={drillDown.columns}
      summary={drillDown.summary}
      onRowClick={drillDown.onRowClick}
      loading={drillDown.loading}
    />
    </>
  );
};

export default DashboardV2AllServices;
